import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Home, Menu, MessageSquare, Package, Plus, Edit3, Tag, Trash2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { isAdminUser } from "../services/admin";
import PageHero from "../components/PageHero";
import heroImage from "../assets/zeamays.png";
import {
  getProductsAdmin,
  createProductAdmin,
  updateProductAdmin,
  deleteProductAdmin,
} from "../services/productsAdmin";
import { ensureBucket, uploadProductImage } from "../services/storage";
import ProductImage from "../components/ProductImage";
import { buildProductPayload } from "../utils/productUtils";

const navItems = [
  { label: "Dashboard", href: "/admin", icon: Home },
  { label: "Orders", href: "/admin/orders", icon: Menu },
  { label: "Blog", href: "/admin/blog", icon: Package },
  { label: "Products", href: "/admin/products", icon: Package },
  { label: "Reviews", href: "/admin/reviews", icon: MessageSquare },
  { label: "Promotions", href: "/admin/promotions", icon: Tag },
];

const emptyForm = {
  name: "",
  slug: "",
  description: "",
  price: 0,
  image_url: "",
  category: "",
  business_line: "Pantry",
  stock: 0,
  featured: false,
  is_active: true,
};

const emptySize = () => ({ size_name: "", price: "" });

const AdminProducts = () => {
  const { user, loading } = useAuth();
  const [authorized, setAuthorized] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [sizes, setSizes] = useState([]);
  const [editingId, setEditingId] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState("");
  const [savedProductSlug, setSavedProductSlug] = useState("");

  const loadProducts = async () => {
    try {
      setError("");
      setLoadingProducts(true);
      const rows = await getProductsAdmin();
      setProducts(rows);
    } catch (err) {
      setError(err.message || "Unable to load products.");
    } finally {
      setLoadingProducts(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      if (!user) {
        if (!cancelled) setAuthorized(false);
        if (!cancelled) setCheckingAccess(false);
        return;
      }

      try {
        const adminAccess = await isAdminUser(user);
        if (!cancelled) setAuthorized(adminAccess);

        if (adminAccess) {
          await loadProducts();
        }
      } catch (err) {
        if (!cancelled) setError(err.message || "Unable to initialize admin products.");
      } finally {
        if (!cancelled) setCheckingAccess(false);
      }
    };

    init();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const startEdit = (product) => {
    setEditingId(product.id);
    setForm({
      name: product.name || "",
      slug: product.slug || "",
      description: product.description || "",
      price: product.price || 0,
      image_url: product.image_url || "",
      category: product.category || "",
      business_line: product.business_line || "Pantry",
      stock: product.stock ?? 0,
      featured: Boolean(product.featured),
      is_active: Boolean(product.is_active),
    });
    setSizes((product.product_sizes || []).map((size) => ({ size_name: size.size_name || "", price: size.price ?? "" })));
    setPreviewUrl(product.image_url || "");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resetForm = () => {
    setEditingId("");
    setForm(emptyForm);
    setSizes([]);
    setError("");
    setSelectedFile(null);
    if (previewUrl && previewUrl.startsWith("blob:")) {
      try { URL.revokeObjectURL(previewUrl); } catch (e) {}
    }
    setPreviewUrl("");
    setUploading(false);
    setUploadProgress(0);
    setUploadError("");
    setSavedProductSlug("");
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((c) => ({ ...c, [name]: type === "checkbox" ? checked : value }));
  };

  const updateSize = (index, field, value) => {
    setSizes((current) => current.map((size, sizeIndex) => sizeIndex === index ? { ...size, [field]: value } : size));
  };

  const removeSize = (index) => setSizes((current) => current.filter((_, sizeIndex) => sizeIndex !== index));

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    setUploadError("");
    try {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    } catch (err) {
      setPreviewUrl("");
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadError("No file selected.");
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadError("");

    try {
      // attempt to ensure bucket exists (may be a no-op in client)
      await ensureBucket();
      const publicUrl = await uploadProductImage(selectedFile, (p) => setUploadProgress(p));
      setForm((c) => ({ ...c, image_url: publicUrl }));
      setPreviewUrl(publicUrl);
      setSelectedFile(null);
    } catch (err) {
      setUploadError(err.message || "Upload failed.");
    } finally {
      setUploading(false);
      setUploadProgress(100);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    try {
      const payload = buildProductPayload({
        name: String(form.name || "").trim(),
        slug: String(form.slug || "").trim(),
        description: String(form.description || "").trim(),
        price: Number(form.price) || 0,
        image_url: String(form.image_url || "").trim(),
        category: String(form.category || "").trim(),
        business_line: form.business_line,
        stock: Number(form.stock) || 0,
        featured: Boolean(form.featured),
        is_active: Boolean(form.is_active),
      });

      if (editingId) {
        await updateProductAdmin(editingId, payload, sizes);
        setSavedProductSlug(payload.slug || form.slug || "");
      } else {
        const createdProduct = await createProductAdmin(payload, sizes);
        setSavedProductSlug(createdProduct?.slug || payload.slug || "");
      }

      await loadProducts();
      setSelectedFile(null);
      setSizes([]);
      setForm(emptyForm);
      setEditingId("");
    } catch (err) {
      setError(err.message || "Unable to save product.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product? This action cannot be undone.")) return;
    setSaving(true);
    setError("");

    try {
      await deleteProductAdmin(id);
      await loadProducts();
    } catch (err) {
      setError(err.message || "Unable to delete product.");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (product) => {
    try {
      await updateProductAdmin(product.id, { is_active: !product.is_active });
      await loadProducts();
    } catch (err) {
      setError(err.message || "Unable to update product status.");
    }
  };

  if (loading || checkingAccess) {
    return (
      <div className="min-h-screen bg-lime-50 px-6 py-28 text-center text-slate-700">
        <p className="text-lg font-semibold">Checking admin access...</p>
      </div>
    );
  }

  if (!authorized) {
    return (
      <div className="min-h-screen bg-lime-50 px-6 py-28 text-center text-slate-700">
        <div className="mx-auto max-w-xl rounded-lg border border-red-200 bg-white p-8 shadow-sm">
          <h1 className="text-3xl font-semibold text-red-700">Access denied</h1>
          <p className="mt-4 text-lg">This page is only available to authorized admin accounts.</p>
          <Link to="/" className="mt-6 inline-block rounded-md bg-red-700 px-5 py-3 text-white">Back to home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7fbef] pt-16 text-slate-900">
      <div className="lg:flex">
        <aside className="fixed bottom-0 left-0 top-16 hidden w-64 border-r border-lime-200 bg-white lg:block">
          <div className="flex h-full flex-col px-5 py-6">
            <div className="border-b border-lime-200 pb-6">
              <p className="text-xs font-bold uppercase tracking-[0.28em] text-red-700">Zea Mays</p>
              <h2 className="mt-2 text-2xl font-bold text-slate-950">Admin</h2>
            </div>
            <nav className="mt-6 grid gap-2">
              {navItems.map((item) => (
                <Link key={item.label} to={item.href} className="flex items-center gap-3 rounded-md px-3 py-3 text-sm font-semibold text-slate-700 hover:bg-lime-100 hover:text-red-700">
                  <item.icon size={18} />
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>
        </aside>

        <main className="w-full px-4 py-6 sm:px-6 lg:ml-64 lg:px-8 lg:py-8">
          <div className="mx-auto max-w-7xl">
            <div className="mb-8">
              <PageHero
                eyebrow="Product studio"
                title="Shape the catalog with a clean product workspace."
                description="Add products, manage sizes, update stock, and keep the storefront fresh with a polished admin flow."
                image={heroImage}
                imageAlt="Zea Mays product management"
                primaryAction={{ to: "/admin", label: "Back to Dashboard" }}
                secondaryAction={{ to: "/products", label: "View Storefront" }}
                stats={[
                  { label: "Products", value: `${products.length}` },
                  { label: "Active", value: `${products.filter((product) => product.is_active).length}` },
                  { label: "Hidden", value: `${products.filter((product) => !product.is_active).length}` },
                  { label: "Sizes", value: "Custom" },
                ]}
                compact
              />
            </div>

            <div className="mb-6 flex flex-col gap-3 border-b border-lime-200 pb-6 md:flex-row md:items-end md:justify-between">
              <div>
                <p className="text-sm font-bold uppercase tracking-[0.28em] text-red-700">Products</p>
                <h2 className="mt-2 text-3xl font-bold text-slate-950">Manage Products</h2>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">Create, edit, and delete products in the store catalog.</p>
              </div>
              <div className="flex gap-2">
                <button onClick={resetForm} className="inline-flex items-center gap-2 rounded-md bg-red-700 px-4 py-2 text-sm font-semibold text-white"><Plus size={16} /> Add Product</button>
              </div>
            </div>

            {error ? <div className="mb-6 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div> : null}

            {savedProductSlug ? (
              <div className="mb-6 rounded-md border border-lime-200 bg-lime-50 p-4 text-sm text-lime-800">
                Product saved. View it on the catalog page at{' '}
                <Link to={`/product/${savedProductSlug}`} className="font-semibold underline">
                  /product/{savedProductSlug}
                </Link>
              </div>
            ) : null}

            {loadingProducts ? (
              <div className="mb-6 rounded-md border border-lime-200 bg-white p-6 text-sm text-slate-700">Loading products…</div>
            ) : null}

            <form onSubmit={handleSave} className="mb-6 grid gap-4 sm:grid-cols-2">
              <input name="name" value={form.name} onChange={handleChange} placeholder="Name" className="rounded-md border px-3 py-2" />
              <input name="slug" value={form.slug} onChange={handleChange} placeholder="Slug" className="rounded-md border px-3 py-2" />
              <input name="price" value={form.price} onChange={handleChange} type="number" placeholder="Price" className="rounded-md border px-3 py-2" />
              <input name="stock" value={form.stock} onChange={handleChange} type="number" placeholder="Stock" className="rounded-md border px-3 py-2" />
              <select name="business_line" value={form.business_line} onChange={handleChange} required className="rounded-md border px-3 py-2">
                <option value="Pantry">Business Line: Pantry</option>
                <option value="ProBioDelights">Business Line: ProBioDelights</option>
              </select>
              <input name="category" value={form.category} onChange={handleChange} placeholder="Category" className="rounded-md border px-3 py-2" />
              <div className="col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-2">Image</label>
                {previewUrl ? (
                  <div className="mb-2 flex aspect-[4/3] items-center justify-center overflow-hidden rounded-md border border-lime-200 bg-lime-50">
                    <ProductImage src={previewUrl} alt="Preview" className="h-full w-full" fit="contain" />
                  </div>
                ) : null}

                <div className="flex items-center gap-2">
                  <input id="product-image-file" type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
                  <label htmlFor="product-image-file" className="rounded-md border px-4 py-2 text-sm cursor-pointer">Choose File</label>
                  <button type="button" onClick={handleUpload} disabled={uploading || !selectedFile} className="rounded-md bg-red-700 px-4 py-2 text-white">{uploading ? `Uploading (${uploadProgress}%)` : "Upload Image"}</button>
                  <button type="button" onClick={() => { setSelectedFile(null); setPreviewUrl(form.image_url || ""); setUploadError(""); }} className="rounded-md border px-4 py-2">Clear</button>
                </div>

                {uploadError ? <div className="mt-2 text-sm text-red-600">{uploadError}</div> : null}

                <div className="mt-2 text-sm text-slate-500">Recommended upload size: 1000×1000px square images for the best results.</div>
                <div className="mt-2 text-sm text-slate-500">Or paste image URL:</div>
                <input name="image_url" value={form.image_url} onChange={handleChange} placeholder="Image URL" className="rounded-md border px-3 py-2 mt-2 w-full" />
              </div>
              <textarea name="description" value={form.description} onChange={handleChange} placeholder="Description" className="rounded-md border px-3 py-2 col-span-2" />
              <div className="col-span-2 rounded-md border border-lime-200 bg-lime-50 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h3 className="font-semibold text-slate-900">Product sizes</h3>
                    <p className="text-sm text-slate-600">Sizes override the base price on the product page. Leave empty to use the base price.</p>
                  </div>
                  <button type="button" onClick={() => setSizes((current) => [...current, emptySize()])} className="inline-flex items-center gap-2 rounded-md border border-red-200 bg-white px-3 py-2 text-sm font-semibold text-red-700"><Plus size={14} /> Add size</button>
                </div>
                {sizes.length ? <div className="mt-4 space-y-3">
                  {sizes.map((size, index) => (
                    <div key={`${index}-${size.size_name}`} className="grid gap-3 sm:grid-cols-[1fr_180px_auto]">
                      <input value={size.size_name} onChange={(event) => updateSize(index, "size_name", event.target.value)} placeholder="Size name (e.g. 500g)" className="rounded-md border bg-white px-3 py-2" />
                      <input value={size.price} onChange={(event) => updateSize(index, "price", event.target.value)} type="number" min="0" step="0.01" placeholder="Price" className="rounded-md border bg-white px-3 py-2" />
                      <button type="button" onClick={() => removeSize(index)} className="rounded-md border border-red-200 px-3 py-2 text-sm font-semibold text-red-700">Remove</button>
                    </div>
                  ))}
                </div> : null}
              </div>
              <div className="col-span-2 flex flex-wrap gap-6">
                <label className="flex items-center gap-2"><input type="checkbox" name="featured" checked={form.featured} onChange={handleChange} /> Featured product</label>
                <label className="flex items-center gap-2"><input type="checkbox" name="is_active" checked={form.is_active} onChange={handleChange} /> Active</label>
              </div>
              <div className="col-span-2 flex gap-2">
                <button type="submit" disabled={saving} className="rounded-md bg-red-700 px-4 py-2 text-white">{editingId ? "Save Changes" : "Create Product"}</button>
                <button type="button" onClick={resetForm} className="rounded-md border px-4 py-2">Cancel</button>
              </div>
            </form>

            <div className="rounded-lg border border-lime-200 bg-white p-4 shadow-sm">
              <div className="overflow-x-auto">
                <table className="min-w-[980px] w-full text-left text-sm">
                  <thead className="bg-lime-50 text-xs font-bold uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-5 py-3">Product Name</th>
                      <th className="px-5 py-3">Business Line</th>
                      <th className="px-5 py-3">Category</th>
                      <th className="px-5 py-3">Price</th>
                      <th className="px-5 py-3">Stock</th>
                      <th className="px-5 py-3">Featured</th>
                      <th className="px-5 py-3">Active</th>
                      <th className="px-5 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-lime-100">
                    {products.length ? (
                      products.map((product) => (
                        <tr key={product.id} className="align-middle">
                          <td className="px-5 py-4 font-semibold text-slate-950">{product.name}</td>
                          <td className="px-5 py-4">{product.business_line || "Pantry"}</td>
                          <td className="px-5 py-4">{product.category || "-"}</td>
                          <td className="px-5 py-4">{product.product_sizes?.length ? product.product_sizes.map((size) => `${size.size_name} (₦${Number(size.price).toLocaleString()})`).join(", ") : `₦${Number(product.price || 0).toLocaleString()}`}</td>
                          <td className="px-5 py-4">{product.stock ?? 0}</td>
                          <td className="px-5 py-4">{product.featured ? "Yes" : "No"}</td>
                          <td className="px-5 py-4">{product.is_active ? "Yes" : "No"}</td>
                          <td className="px-5 py-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button onClick={() => startEdit(product)} className="inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm"><Edit3 size={14} /> Edit</button>
                              <button onClick={() => toggleActive(product)} className="inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm">Toggle</button>
                              <button onClick={() => handleDelete(product.id)} className="inline-flex items-center gap-2 rounded-md bg-red-700 px-3 py-2 text-sm text-white"><Trash2 size={14} /> Delete</button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="px-5 py-10 text-center text-slate-500">No products found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminProducts;
